import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign, RefreshCw, Save, RotateCcw, ArrowLeftRight, Copy, Trash2, Plus,
  Download, Printer, FileSpreadsheet, FileText, Clock, ShieldCheck, History,
  AlertCircle, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Wifi, WifiOff,
} from "lucide-react";

const RANGES = [
  { label: "1 – 100",   max: 100 },
  { label: "1 – 500",   max: 500 },
  { label: "1 – 1000",  max: 1000 },
  { label: "1 – 5000",  max: 5000 },
  { label: "1 – 10000", max: 10000 },
];
const PAGE_SIZES = [10, 25, 50, 100, 250];

const fmt = (n, dp = 4) => {
  if (n === null || n === undefined || isNaN(n)) return "";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  });
};

const SortIcon = ({ col, sortBy, sortDir }) => (
  sortBy !== col ? <ArrowUpDown className="w-3 h-3 inline text-slate-400 ml-1" /> :
  sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline text-indigo-600 ml-1" /> :
  <ArrowDown className="w-3 h-3 inline text-indigo-600 ml-1" />
);

/* ============================================================
   Main Component
============================================================ */
const CurrencyConverterPanel = ({ user }) => {
  const isAdmin = user?.role === "admin";
  // Processing Team members with the currency_converter sub-panel grant can
  // edit the manual billing exchange rate (same permission as admin).
  // Only history remains admin-only.
  const canEdit = true; // any user rendering this panel already has read access
                        // via processing_team_sub_panels.currency_converter

  const [currencies, setCurrencies] = useState([]);
  const [manualRates, setManualRates] = useState([]); // [{currency, rate, updated_at, updated_by, symbol, name}]
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);

  const activeRateEntry = useMemo(
    () => manualRates.find((r) => r.currency === selectedCurrency) || null,
    [manualRates, selectedCurrency]
  );
  const activeRate = activeRateEntry?.rate ?? null;
  const activeSymbol = activeRateEntry?.symbol || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, mRes] = await Promise.all([
        axios.get(`${API}/currency/currencies`, { headers: getAuthHeader() }),
        axios.get(`${API}/currency/manual-rates`, { headers: getAuthHeader() }),
      ]);
      setCurrencies(cRes.data.currencies || []);
      setManualRates(mRes.data.rates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load currency data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="currency-loading">
        Loading currency converter…
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-4 md:p-6 space-y-6" data-testid="currency-converter-panel">
      {/* ---------- Page Header ---------- */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2" data-testid="currency-heading">
            <DollarSign className="w-8 h-8 text-indigo-600" />
            Currency Converter for Billing
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            <span className="font-semibold text-slate-700">Manual Exchange Rate</span> is used for official billing.{" "}
            <span className="font-semibold text-slate-700">Live Exchange Rate</span> is for market reference only.
          </p>
        </div>
        <Badge variant="outline" className="w-fit text-xs bg-white border-slate-300">
          <ShieldCheck className="w-3 h-3 mr-1 text-emerald-600" />
          {isAdmin ? "Administrator" : "Processing Team"}
        </Badge>
      </div>

      {/* ---------- Section 1 & 2: Rates side-by-side on desktop ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ManualRateCard
          currencies={currencies}
          manualRates={manualRates}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
          canEdit={canEdit}
          reload={load}
        />
        <LiveRateCard
          currencies={currencies}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
          manualRate={activeRate}
        />
      </div>

      {/* ---------- Section 3: Converter Calculator ---------- */}
      <ConverterCalculator
        currencies={currencies}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        manualRate={activeRate}
        symbol={activeSymbol}
      />

      {/* ---------- Section 4: Quick Manual Multi-Value ---------- */}
      <QuickManualConversion
        currencies={currencies}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        manualRate={activeRate}
        symbol={activeSymbol}
      />

      {/* ---------- Section 5: Automatic Billing Reference Table ---------- */}
      <AutoReferenceTable
        currencies={currencies}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        manualRate={activeRate}
        symbol={activeSymbol}
      />

      {/* ---------- Section 6: Exchange Rate History (admin only) ---------- */}
      {isAdmin && <ExchangeRateHistoryCard />}
    </div>
  );
};

/* ============================================================
   Section 1 — Manual Exchange Rate Card
============================================================ */
const ManualRateCard = ({ currencies, manualRates, selectedCurrency, setSelectedCurrency, canEdit, reload }) => {
  const active = manualRates.find((r) => r.currency === selectedCurrency);
  const [rateInput, setRateInput] = useState(active?.rate != null ? String(active.rate) : "");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    setRateInput(active?.rate != null ? String(active.rate) : "");
  }, [selectedCurrency, active?.rate]);

  const validate = () => {
    const v = parseFloat(rateInput);
    if (isNaN(v)) { toast.error("Please enter a valid number"); return null; }
    if (v <= 0) { toast.error("Rate must be greater than zero"); return null; }
    return v;
  };

  const performSave = async () => {
    const v = validate(); if (v === null) return;
    setSaving(true);
    try {
      await axios.post(`${API}/currency/manual-rates`,
        { currency: selectedCurrency, rate: v },
        { headers: getAuthHeader() });
      toast.success(`${selectedCurrency} rate updated to ₹${v}`);
      setConfirmOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onSaveClick = () => {
    if (validate() === null) return;
    // If a rate already exists, require confirmation (billing safety)
    if (active?.rate != null) { setConfirmOpen(true); return; }
    performSave();
  };

  const performReset = async () => {
    try {
      await axios.delete(`${API}/currency/manual-rates/${selectedCurrency}`,
        { headers: getAuthHeader() });
      toast.success(`${selectedCurrency} rate reset`);
      setResetOpen(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <Card className="border-indigo-200 shadow-sm" data-testid="manual-rate-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            Manual Billing Exchange Rate
          </CardTitle>
          <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">OFFICIAL</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs text-slate-600">Currency</Label>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="mt-1" data-testid="manual-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`manual-currency-opt-${c.code}`}>
                    <span className="font-mono">{c.code}</span>
                    <span className="text-slate-500 ml-2">({c.symbol}) {c.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3">
            <Label className="text-xs text-slate-600">1 {selectedCurrency} = <span className="font-semibold">? INR</span></Label>
            <Input
              type="number" step="0.0001" min="0"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              placeholder="e.g. 95.20"
              disabled={!canEdit}
              className="mt-1 text-lg font-semibold"
              data-testid="manual-rate-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={onSaveClick} disabled={!canEdit || saving}
                  data-testid="manual-rate-save-btn">
            <Save className="w-4 h-4 mr-1" />
            {active?.rate != null ? "Update Rate" : "Save Rate"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setRateInput(active?.rate != null ? String(active.rate) : "")}
                  disabled={!canEdit} data-testid="manual-rate-revert-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Revert
          </Button>
          {active?.rate != null && (
            <Button size="sm" variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setResetOpen(true)} disabled={!canEdit}
                    data-testid="manual-rate-reset-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Reset
            </Button>
          )}
          {!canEdit && (
            <span className="text-xs text-slate-500 ml-auto">Read-only</span>
          )}
        </div>

        {active?.rate != null && (
          <div className="p-3 rounded-md bg-indigo-50/60 border border-indigo-100">
            <div className="text-2xl font-bold text-indigo-900" data-testid="manual-rate-display">
              1 {selectedCurrency} = ₹{fmt(active.rate, 4)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Last updated {new Date(active.updated_at).toLocaleString()} · by {active.updated_by || "—"}
            </div>
          </div>
        )}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="manual-rate-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm rate update</DialogTitle>
            <DialogDescription>
              You&apos;re about to change the official <b>{selectedCurrency}</b> billing rate from{" "}
              <b>₹{fmt(active?.rate, 4)}</b> to <b>₹{fmt(parseFloat(rateInput), 4)}</b>.
              This will affect all future invoices and billing calculations.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={performSave} disabled={saving}
                    data-testid="manual-rate-confirm-btn">
              Confirm Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="manual-rate-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset {selectedCurrency} rate?</DialogTitle>
            <DialogDescription>
              This clears the manual billing rate for {selectedCurrency}. Existing invoices are unaffected,
              but new conversions will fall back to the Live Rate until a new manual rate is set.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={performReset}>Reset Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* ============================================================
   Section 2 — Live Exchange Rate Card
============================================================ */
const LiveRateCard = ({ currencies, selectedCurrency, setSelectedCurrency, manualRate }) => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRate = useCallback(async (cc) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/currency/live/${cc}`, { headers: getAuthHeader() });
      setLive(res.data);
    } catch (e) {
      setLive({ status: "error", error: e?.response?.data?.detail || "fetch failed",
               currency: cc, rate: null, fetched_at: new Date().toISOString(),
               source: "open.er-api.com" });
    } finally { setLoading(false); }
  }, []);

  // Auto-fetch when the selected currency changes
  useEffect(() => { fetchRate(selectedCurrency); }, [selectedCurrency, fetchRate]);

  const status = live?.status;
  const rate = live?.rate;
  const fetchedAt = live?.fetched_at ? new Date(live.fetched_at) : null;

  return (
    <Card className="border-emerald-200 shadow-sm" data-testid="live-rate-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
            {status === "ok" ? <Wifi className="w-4 h-4 text-emerald-600" /> : <WifiOff className="w-4 h-4 text-amber-600" />}
            Live Exchange Rate (Market Reference)
          </CardTitle>
          <Badge className={`text-[10px] ${status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {status === "ok" ? "LIVE" : "OFFLINE"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs text-slate-600">Currency</Label>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="mt-1" data-testid="live-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="font-mono">{c.code}</span> <span className="text-slate-500">({c.symbol})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3 flex gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => fetchRate(selectedCurrency)}
                    disabled={loading} data-testid="live-fetch-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Fetch Current Rate
            </Button>
            <Button size="sm" variant="outline" onClick={() => fetchRate(selectedCurrency)}
                    disabled={loading} data-testid="live-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {status === "ok" && rate != null ? (
          <div className="p-3 rounded-md bg-emerald-50/60 border border-emerald-100">
            <div className="text-2xl font-bold text-emerald-900" data-testid="live-rate-display">
              1 {selectedCurrency} = ₹{fmt(rate, 4)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
              <span><Clock className="w-3 h-3 inline mr-1" />{fetchedAt?.toLocaleString()}</span>
              <span>Source: {live?.source}</span>
              {live?.cached && <span className="text-slate-400">(cached · &lt; 60s)</span>}
            </div>
          </div>
        ) : (
          <div className="p-3 rounded-md bg-amber-50/60 border border-amber-200" data-testid="live-rate-fallback">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-900">Live exchange rate unavailable</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Using Manual Billing Rate: {manualRate != null
                    ? <span className="font-semibold">1 {selectedCurrency} = ₹{fmt(manualRate, 4)}</span>
                    : <span className="italic">not set</span>}
                </div>
                {live?.error && <div className="text-[10px] text-amber-700 mt-1">Details: {live.error}</div>}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ============================================================
   Section 3 — Converter Calculator
============================================================ */
const ConverterCalculator = ({ currencies, selectedCurrency, setSelectedCurrency, manualRate, symbol }) => {
  const [mode, setMode] = useState("inr-to-foreign"); // or "foreign-to-inr"
  const [amount, setAmount] = useState("");

  const converted = useMemo(() => {
    const v = parseFloat(amount);
    if (isNaN(v) || !manualRate) return null;
    return mode === "inr-to-foreign" ? v / manualRate : v * manualRate;
  }, [amount, manualRate, mode]);

  const swap = () => setMode((m) => m === "inr-to-foreign" ? "foreign-to-inr" : "inr-to-foreign");
  const clear = () => setAmount("");
  const copyResult = () => {
    if (converted === null) return;
    navigator.clipboard.writeText(fmt(converted, 4));
    toast.success("Copied to clipboard");
  };

  const fromLabel = mode === "inr-to-foreign" ? "INR (₹)" : `${selectedCurrency} (${symbol})`;
  const toLabel   = mode === "inr-to-foreign" ? `${selectedCurrency} (${symbol})` : "INR (₹)";

  return (
    <Card data-testid="converter-calculator-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-indigo-600" /> Currency Converter Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!manualRate && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Set a manual rate above to enable conversion.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-11 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Currency</Label>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="mt-1" data-testid="calc-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs">From ({fromLabel})</Label>
            <Input type="number" step="0.0001" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   className="mt-1 text-lg font-semibold" placeholder="0.00"
                   data-testid="calc-from-input" />
          </div>
          <div className="md:col-span-1 flex justify-center">
            <Button variant="outline" size="icon" onClick={swap} data-testid="calc-swap-btn" title="Swap direction">
              <ArrowLeftRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs">To ({toLabel})</Label>
            <div className="mt-1 flex gap-2">
              <Input readOnly value={converted !== null ? fmt(converted, 4) : ""}
                     className="text-lg font-semibold bg-slate-50" placeholder="—"
                     data-testid="calc-to-output" />
              <Button variant="outline" size="icon" onClick={copyResult} disabled={converted === null}
                      data-testid="calc-copy-btn" title="Copy result">
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={clear} data-testid="calc-clear-btn" title="Clear">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        {manualRate && amount && converted !== null && (
          <div className="text-xs text-slate-500 mt-1">
            Using manual rate: <span className="font-semibold">1 {selectedCurrency} = ₹{fmt(manualRate, 4)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ============================================================
   Section 4 — Quick Manual Multi-Value Conversion
============================================================ */
const QuickManualConversion = ({ currencies, selectedCurrency, setSelectedCurrency, manualRate, symbol }) => {
  const [reverse, setReverse] = useState(false); // false = INR → Foreign, true = Foreign → INR
  const [rows, setRows] = useState(() => Array.from({ length: 10 }, () => ({ input: "" })));

  const compute = (inp) => {
    const v = parseFloat(inp);
    if (isNaN(v) || !manualRate) return null;
    return reverse ? v * manualRate : v / manualRate;
  };

  const setRow = (i, v) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], input: v };
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, { input: "" }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const clearAll = () => setRows(Array.from({ length: 10 }, () => ({ input: "" })));

  // Excel-style paste: split by newline into rows
  const handlePaste = (i, e) => {
    const text = e.clipboardData?.getData("text") || "";
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l, idx, arr) => l !== "" || idx < arr.length - 1);
    // strip trailing empties
    while (lines.length && lines[lines.length - 1] === "") lines.pop();
    if (lines.length <= 1) return; // let default single-line paste happen
    e.preventDefault();
    setRows((prev) => {
      const next = [...prev];
      for (let k = 0; k < lines.length; k++) {
        const target = i + k;
        if (target >= next.length) next.push({ input: lines[k] });
        else next[target] = { ...next[target], input: lines[k] };
      }
      return next;
    });
    toast.success(`Pasted ${lines.length} values`);
  };

  const fromLabel = reverse ? `${selectedCurrency} (${symbol})` : "INR (₹)";
  const toLabel   = reverse ? "INR (₹)" : `${selectedCurrency} (${symbol})`;

  const populatedRows = rows
    .map((r, i) => ({ sn: i + 1, input: r.input, output: compute(r.input) }))
    .filter((r) => r.input !== "" && r.output !== null);

  // ---- Exports ----
  const exportExcel = () => {
    const data = [
      ["S.No", fromLabel, toLabel],
      ...populatedRows.map((r) => [r.sn, parseFloat(r.input), Number(r.output.toFixed(4))]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Quick Conversion");
    XLSX.writeFile(wb, `quick-conversion-${selectedCurrency}.xlsx`);
  };
  const exportCSV = () => {
    const lines = [`S.No,${fromLabel},${toLabel}`,
      ...populatedRows.map((r) => `${r.sn},${r.input},${r.output.toFixed(4)}`)];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `quick-conversion-${selectedCurrency}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const copyTable = () => {
    const lines = [`S.No\t${fromLabel}\t${toLabel}`,
      ...populatedRows.map((r) => `${r.sn}\t${r.input}\t${r.output.toFixed(4)}`)];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success(`Copied ${populatedRows.length} rows to clipboard`);
  };
  const printOrPdf = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow popups to print");
    const html = `<html><head><title>Quick Conversion</title>
      <style>body{font-family:Arial;padding:20px}h1{font-size:18px}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:6px;text-align:right;font-size:12px}
      th{background:#f1f5f9;text-align:left}td:first-child,th:first-child{text-align:center;width:60px}</style></head>
      <body><h1>Quick Currency Conversion — ${selectedCurrency}</h1>
      <p style="font-size:12px;color:#555">Manual Rate: 1 ${selectedCurrency} = ₹${fmt(manualRate, 4)} · Direction: ${reverse ? "Foreign → INR" : "INR → Foreign"}</p>
      <table><thead><tr><th>S.No</th><th>${fromLabel}</th><th>${toLabel}</th></tr></thead>
      <tbody>${populatedRows.map((r) => `<tr><td>${r.sn}</td><td>${r.input}</td><td>${fmt(r.output, 4)}</td></tr>`).join("")}</tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`;
    w.document.write(html); w.document.close();
  };

  return (
    <Card data-testid="quick-conversion-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Quick Manual INR to Foreign Currency Conversion</CardTitle>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1 text-xs">
              <Label className="text-xs text-slate-600">Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger className="h-8 w-28" data-testid="quick-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant={reverse ? "outline" : "default"}
                    className={!reverse ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                    onClick={() => setReverse(false)} data-testid="quick-mode-inr">
              INR → {selectedCurrency}
            </Button>
            <Button size="sm" variant={reverse ? "default" : "outline"}
                    className={reverse ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                    onClick={() => setReverse(true)} data-testid="quick-mode-foreign">
              {selectedCurrency} → INR
            </Button>
          </div>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Tip: paste a column of numbers from Excel into any row — the table will auto-expand.
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="quick-add-row"><Plus className="w-4 h-4 mr-1" />Add Row</Button>
          <Button size="sm" variant="outline" onClick={clearAll} data-testid="quick-clear-all"><Trash2 className="w-4 h-4 mr-1" />Clear All</Button>
          <span className="flex-1" />
          <Button size="sm" variant="outline" onClick={exportExcel} disabled={!populatedRows.length} data-testid="quick-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
          </Button>
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={!populatedRows.length} data-testid="quick-export-csv">
            <FileText className="w-4 h-4 mr-1" />CSV
          </Button>
          <Button size="sm" variant="outline" onClick={printOrPdf} disabled={!populatedRows.length} data-testid="quick-print">
            <Printer className="w-4 h-4 mr-1" />Print / PDF
          </Button>
          <Button size="sm" variant="outline" onClick={copyTable} disabled={!populatedRows.length} data-testid="quick-copy">
            <Copy className="w-4 h-4 mr-1" />Copy
          </Button>
        </div>

        <div className="border rounded-md overflow-x-auto bg-white">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100">
              <TableRow>
                <TableHead className="w-14 text-center">S.No</TableHead>
                <TableHead>{fromLabel}</TableHead>
                <TableHead>{toLabel}</TableHead>
                <TableHead className="w-16 text-right">—</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const val = compute(r.input);
                return (
                  <TableRow key={i} data-testid={`quick-row-${i}`}>
                    <TableCell className="text-center text-xs text-slate-500">{i + 1}</TableCell>
                    <TableCell className="p-1">
                      <Input type="number" step="0.0001" value={r.input}
                             onChange={(e) => setRow(i, e.target.value)}
                             onPaste={(e) => handlePaste(i, e)}
                             className="h-8 font-mono" placeholder="0"
                             data-testid={`quick-input-${i}`} />
                    </TableCell>
                    <TableCell className="p-1">
                      <div className="h-8 flex items-center px-2 bg-slate-50 rounded border border-slate-200 font-mono text-sm text-slate-800"
                           data-testid={`quick-output-${i}`}>
                        {val !== null ? fmt(val, 4) : <span className="text-slate-300">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {rows.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                                onClick={() => removeRow(i)} data-testid={`quick-remove-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ============================================================
   Section 5 — Automatic Billing Reference Table
============================================================ */
const AutoReferenceTable = ({ currencies, selectedCurrency, setSelectedCurrency, manualRate, symbol }) => {
  const [rangeMax, setRangeMax] = useState(1000);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("sn");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => { setPage(1); }, [rangeMax, q, sortBy, sortDir, pageSize]);

  const allRows = useMemo(() => {
    if (!manualRate) return [];
    return Array.from({ length: rangeMax }, (_, i) => ({
      sn: i + 1,
      inr: i + 1,
      foreign: (i + 1) / manualRate,
    }));
  }, [manualRate, rangeMax]);

  const filtered = useMemo(() => {
    const query = q.trim();
    if (!query) return allRows;
    return allRows.filter((r) =>
      String(r.inr).includes(query) || r.foreign.toFixed(4).includes(query)
    );
  }, [allRows, q]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  const setSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  // ---- Exports ----
  const exportExcel = () => {
    const data = [
      ["S.No", "INR (₹)", `${selectedCurrency} (${symbol})`],
      ...sorted.map((r) => [r.sn, r.inr, Number(r.foreign.toFixed(4))]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Billing Reference");
    XLSX.writeFile(wb, `billing-reference-${selectedCurrency}-1-${rangeMax}.xlsx`);
  };
  const exportCSV = () => {
    const lines = [`S.No,INR,${selectedCurrency}`,
      ...sorted.map((r) => `${r.sn},${r.inr},${r.foreign.toFixed(4)}`)];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `billing-reference-${selectedCurrency}-1-${rangeMax}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const copyTable = () => {
    const lines = [`S.No\tINR\t${selectedCurrency}`,
      ...sorted.map((r) => `${r.sn}\t${r.inr}\t${r.foreign.toFixed(4)}`)];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success(`Copied ${sorted.length} rows`);
  };
  const printOrPdf = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow popups to print");
    const html = `<html><head><title>Billing Reference</title>
      <style>body{font-family:Arial;padding:20px}h1{font-size:18px}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:4px 8px;text-align:right;font-size:11px}
      th{background:#f1f5f9;text-align:left}td:first-child,th:first-child{text-align:center;width:60px}</style></head>
      <body><h1>Billing Reference — 1 ${selectedCurrency} = ₹${fmt(manualRate, 4)}</h1>
      <table><thead><tr><th>S.No</th><th>INR</th><th>${selectedCurrency}</th></tr></thead>
      <tbody>${sorted.map((r) => `<tr><td>${r.sn}</td><td>${r.inr}</td><td>${fmt(r.foreign, 4)}</td></tr>`).join("")}</tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`;
    w.document.write(html); w.document.close();
  };

  if (!manualRate) {
    return (
      <Card data-testid="auto-ref-card">
        <CardHeader><CardTitle className="text-base">Automatic Billing Reference Table</CardTitle></CardHeader>
        <CardContent><div className="text-sm text-slate-500 italic">Set a manual rate to generate the reference table.</div></CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="auto-ref-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Automatic Billing Reference Table</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <Label className="text-xs">Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger className="h-8 w-24" data-testid="auto-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs">Range</Label>
              <Select value={String(rangeMax)} onValueChange={(v) => setRangeMax(Number(v))}>
                <SelectTrigger className="h-8 w-28" data-testid="auto-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RANGES.map((r) => <SelectItem key={r.max} value={String(r.max)}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                   className="pl-8 h-9" data-testid="auto-search" />
          </div>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-9 w-24" data-testid="auto-pagesize"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportExcel} data-testid="auto-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
          </Button>
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="auto-export-csv">
            <FileText className="w-4 h-4 mr-1" />CSV
          </Button>
          <Button size="sm" variant="outline" onClick={printOrPdf} data-testid="auto-print">
            <Printer className="w-4 h-4 mr-1" />Print / PDF
          </Button>
          <Button size="sm" variant="outline" onClick={copyTable} data-testid="auto-copy">
            <Copy className="w-4 h-4 mr-1" />Copy
          </Button>
        </div>

        <div className="border rounded-md overflow-x-auto bg-white max-h-[420px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead className="w-16 cursor-pointer" onClick={() => setSort("sn")}>
                  S.No <SortIcon col="sn" sortBy={sortBy} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => setSort("inr")}>
                  INR (₹) <SortIcon col="inr" sortBy={sortBy} sortDir={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => setSort("foreign")}>
                  {selectedCurrency} ({symbol}) <SortIcon col="foreign" sortBy={sortBy} sortDir={sortDir} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((r) => (
                <TableRow key={r.sn}>
                  <TableCell className="text-xs text-slate-500">{r.sn}</TableCell>
                  <TableCell className="font-mono text-sm">{r.inr}</TableCell>
                  <TableCell className="font-mono text-sm">{fmt(r.foreign, 4)}</TableCell>
                </TableRow>
              ))}
              {!paged.length && (
                <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6">No matching rows</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500">
          <div>{sorted.length.toLocaleString()} rows · Page {page} of {totalPages}</div>
          <div className="flex gap-1">
            <Button size="icon" variant="outline" className="h-7 w-7"
                    disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="auto-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="outline" className="h-7 w-7"
                    disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="auto-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ============================================================
   Section 6 — Exchange Rate History (admin only)
============================================================ */
const ExchangeRateHistoryCard = () => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/currency/history?limit=500`, { headers: getAuthHeader() });
      setHistory(res.data.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Card data-testid="rate-history-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-slate-600" /> Exchange Rate History
            <Badge variant="outline" className="text-[10px]">Admin Only</Badge>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="history-toggle">
            {open ? "Hide" : "View History"}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-4 text-sm">Loading…</div>
          ) : (
            <div className="border rounded-md overflow-x-auto max-h-[360px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100">
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                    <TableHead className="text-xs">Currency</TableHead>
                    <TableHead className="text-xs">Old Rate</TableHead>
                    <TableHead className="text-xs">New Rate</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                    <TableHead className="text-xs">Updated By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h, i) => {
                    const d = new Date(h.updated_at);
                    return (
                      <TableRow key={i} data-testid={`history-row-${i}`}>
                        <TableCell className="text-xs">{d.toLocaleDateString()}</TableCell>
                        <TableCell className="text-xs font-mono">{d.toLocaleTimeString()}</TableCell>
                        <TableCell className="text-xs font-semibold">{h.currency}</TableCell>
                        <TableCell className="text-xs font-mono">{h.old_rate != null ? `₹${fmt(h.old_rate, 4)}` : "—"}</TableCell>
                        <TableCell className="text-xs font-mono">{h.new_rate != null ? `₹${fmt(h.new_rate, 4)}` : "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] capitalize">{h.action}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{h.updated_by || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!history.length && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6 text-sm">No history yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default CurrencyConverterPanel;
